import { useEffect, useState, useCallback } from 'react'
import isEqual from 'lodash/isEqual'
import { usePrevious } from 'utils/usePrevious'

const EMPTY_OBJECT = {}

interface IUseRefresh {
  /**
   * Timestamp that can be used as a key
   * for triggering re-mounting of React components
   */
  key: number
  /**
   * Reset the key manually to a new timestamp
   */
  resetKey: () => void
}

/**
 * Triggers a refreshed key when value updates after comparing to previous value
 * @param {T} value - The value to watch for updates
 * @example const { key, resetKey } = useRefresh(currentTruck)
 */
export function useRefresh<T extends Object>(value: T = EMPTY_OBJECT as T): IUseRefresh {
  const [key, setKey] = useState<number>(Date.now())
  const prevValue = usePrevious<T | undefined>(value)

  /**
   * When value updates, return a new timestamp/key.
   * Object.keys check is to rule out undefined or empty objects
   */
  useEffect(() => {
    const isEqualValue = isEqual(prevValue, value)
    if (!isEqualValue && value && prevValue) {
      setKey(Date.now())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, prevValue])

  /**
   * Manually sets a new key value
   */
  const resetKey = useCallback(() => {
    setKey(Date.now())
  }, [])

  return {
    key,
    resetKey,
  }
}
